import { isWithinInterval, parseISO } from 'date-fns';
import { makeAutoObservable, reaction, runInAction } from 'mobx';
import moment from 'moment-timezone';

import { rejectExtraActivityAssignment } from 'common/activeElements/modals/ModalDeleteExtraActivity/rejectExtraActivityAssignment';
import { ACTIVITY_RENDER_TYPES } from 'common/ActivityChip/const';
import { replaceSequenceInDriver } from 'common/ExtraActivityTimelineManagement/patchSequence';
import { client } from 'contexts/ApolloClient/apolloClient';
import { standardError } from 'global/commonMessages';
import { notificationStore } from 'global/NotificationStore';
import {
	injectPersonalTimelineMock,
	remapPersonalTimelineMockToCurrentWeek,
} from 'mocks/timeline/personalTimelineMockLoader';
import { FETCH_CREW_MEMBER_HOMEBASE } from 'pages/CrewMemberDetails/gql/fetchCrewMemberHomebase';
import { FETCH_PERSONAL_TIMELINE_V2 } from 'pages/CrewMemberDetails/gql/fetchPersonalTimelineV2';
import {
	isDateInDaysArray,
	isDayInCurrentWeek,
	resetTimestampToUtc,
	TIME_ZONES_ADDITIONS,
} from 'utils/datetime';
import { getCurrentMomentInTimezone } from 'utils/datetime/currentTimeCalculations';

import { mapPersonalTimeline } from './utils/mapPersonalTimeline';
import { getWeekArrayFromMoment } from './utils/weekArray';

class PersonalTimelineStore {
	crewMemberId = '';
	activities = [];
	rawTimelineDriver = null;
	isOnCurrentWeek = true;
	selectedTimezone = null;
	homebaseTimezone = null;
	currentMoment = null;
	timeline = [];
	isInitialized = false;
	isLoading = true;
	isError = false;
	useMockData = false;
	isDeletingExtraActivity = false;

	constructor() {
		const timeZone = TIME_ZONES_ADDITIONS.myTime;
		const current = getCurrentMomentInTimezone(timeZone.value);
		const generatedTimeline = getWeekArrayFromMoment(current, timeZone.value);

		this.selectedTimezone = timeZone;
		this.currentMoment = current;
		this.timeline = generatedTimeline;

		makeAutoObservable(this);
		reaction(
			() => this.timeline, // observable to track
			async (timeline) => {
				if (!timeline) return;
				try {
					this.fetchActivities();
				} catch (e) {
					this.isError = true;
				}
			}
		);
	}

	initializeData = async (crewMemberId) => {
		this.crewMemberId = crewMemberId;
		this.isLoading = true;
		// TO DO - get rid of this request, when global page store for Crew Member Details will be introduced
		try {
			const { data } = await client.query({
				query: FETCH_CREW_MEMBER_HOMEBASE,
				variables: {
					id: this.crewMemberId,
				},
				fetchPolicy: 'no-cache',
			});

			const additionalTimezone =
				data?.getCrewMember?.currentHomeBase === null
					? null
					: {
							name: 'Home base time',
							value: data?.getCrewMember?.currentHomeBase?.city?.timezone,
						};
			this.createAdditionalTimezone(additionalTimezone);
			this.setSelectedTimezone(additionalTimezone);
			await this.fetchActivities();
		} catch (e) {
			notificationStore.setNotification(standardError);
		} finally {
			this.isInitialized = true;
			this.isLoading = false;
		}
	};

	fetchActivities = async () => {
		this.isLoading = true;
		const dayOneStart = resetTimestampToUtc(this.timeline[0]);
		const dayLastFinish = moment(this.timeline[this.timeline.length - 1]).endOf('day');

		const variables = {
			filter: {
				crewMemberId: this.crewMemberId,
				fromTimestamp: dayOneStart,
				toTimestamp: dayLastFinish,
			},
		};
		try {
			const { data } = await client.query({
				query: FETCH_PERSONAL_TIMELINE_V2,
				variables,
				fetchPolicy: 'no-cache',
			});

			const effectiveData = this.useMockData
				? injectPersonalTimelineMock(data, remapPersonalTimelineMockToCurrentWeek())
				: data;

			const driver = effectiveData?.crewMemberTimeline?.results?.[0];
			const activitiesCalculated = mapPersonalTimeline(
				driver,
				this.timeline,
				this.selectedTimezone.value
			);
			runInAction(() => {
				this.isLoading = false;
				this.rawTimelineDriver = driver ?? null;
				this.activities = activitiesCalculated;
			});
		} catch (e) {
			notificationStore.setNotification(standardError);
		}
	};

	mergeExtraActivitySequence = async (sequence) => {
		if (!this.rawTimelineDriver) {
			await this.fetchActivities();
			return false;
		}

		const { driver, didPatch } = replaceSequenceInDriver(this.rawTimelineDriver, sequence);

		if (!didPatch) {
			await this.fetchActivities();
			return false;
		}

		const activitiesCalculated = mapPersonalTimeline(
			driver,
			this.timeline,
			this.selectedTimezone.value
		);

		runInAction(() => {
			this.rawTimelineDriver = driver;
			this.activities = activitiesCalculated;
		});

		return true;
	};

	rejectExtraActivityAssignment = async (workAssignmentKey) => {
		runInAction(() => {
			this.isDeletingExtraActivity = true;
		});

		try {
			const sequence = await rejectExtraActivityAssignment(workAssignmentKey);

			if (!sequence) return false;

			return this.mergeExtraActivitySequence(sequence);
		} catch (error) {
			notificationStore.setNotification(standardError);
			return false;
		} finally {
			runInAction(() => {
				this.isDeletingExtraActivity = false;
			});
		}
	};

	setSelectedTimezone = (nextTimezone) => {
		if (nextTimezone === null) return;
		const currentMomentInTimezone = getCurrentMomentInTimezone(nextTimezone.value);
		const timelineReferencedToNewTimezone = getWeekArrayFromMoment(
			this.timeline[3],
			nextTimezone.value
		);
		runInAction(() => {
			this.selectedTimezone = nextTimezone;
			this.currentMoment = currentMomentInTimezone;
			this.timeline = timelineReferencedToNewTimezone;
		});
	};

	createAdditionalTimezone = (data) => {
		if (data === null) return;
		this.homebaseTimezone = data;
	};

	setTimelineToCurrentWeek = () => {
		this.timeline = getWeekArrayFromMoment(this.currentMoment, this.selectedTimezone.value);
	};

	setTimelineToPreviousWeek = () => {
		const dayFromPreviousWeek = this.timeline[0].clone().subtract(1, 'd');
		this.timeline = getWeekArrayFromMoment(dayFromPreviousWeek, this.selectedTimezone.value);
	};

	setTimelineToNextWeek = () => {
		const dayFromNextWeek = this.timeline[this.timeline.length - 1].clone().add(1, 'd');
		this.timeline = getWeekArrayFromMoment(dayFromNextWeek, this.selectedTimezone.value);
	};

	checkCurrentWeekAndRefetchTimeline = (timestamp) => {
		if (!isDateInDaysArray(timestamp, this.timeline, this.selectedTimezone.value)) {
			return;
		} else this.fetchActivities();
	};

	get isDriverInActiveShift() {
		const activities = this.rawTimelineDriver?.activitiesV2;
		if (!activities?.length) return false;
		const now = new Date();
		const currentActivity = activities.find((item) =>
			isWithinInterval(now, {
				start: parseISO(item.fromTimestamp),
				end: parseISO(item.toTimestamp),
			})
		);
		if (!currentActivity) return false;
		if (currentActivity.activity?.breakType === ACTIVITY_RENDER_TYPES.rest) return false;
		return currentActivity.activity?.shiftId != null;
	}

	get isTimelineInCurrentWeek() {
		return isDayInCurrentWeek(this.timeline[3], this.selectedTimezone.value);
	}

	setUseMockData = (value) => {
		runInAction(() => {
			this.useMockData = value;
		});
		this.fetchActivities();
	};
}

const personalTimelineStore = new PersonalTimelineStore();
export default personalTimelineStore;
